import { z } from 'zod';

import type { BadgeProps } from '@/shared/ui';

/**
 * PlannerActivity status enum — backend integer values.
 *
 * Mirrors the legacy `PlannerActivityStatus` enum in
 * `src/pages/Planner/types/plannerTypes.ts`. Exposed as a const map (not a TS
 * enum) so values can be inlined by the bundler and tree-shaken cleanly.
 *
 * IMPORTANT: numeric values must match the backend exactly. Do not renumber.
 */
export const PlannerActivityStatus = {
  Scheduled: 1,
  InProgress: 2,
  CompletedPendingReview: 3,
  Approved: 4,
  Rejected: 5,
  Cancelled: 6,
} as const;

export type PlannerActivityStatusValue =
  (typeof PlannerActivityStatus)[keyof typeof PlannerActivityStatus];

/**
 * Display labels keyed by integer status value. Used by the status badge, the
 * Select filter, the execution-page review table and confirmation dialogs.
 */
export const plannerActivityStatusLabels: Record<number, string> = {
  [PlannerActivityStatus.Scheduled]: 'Scheduled',
  [PlannerActivityStatus.InProgress]: 'In Progress',
  [PlannerActivityStatus.CompletedPendingReview]: 'Completed (Pending Review)',
  [PlannerActivityStatus.Approved]: 'Approved',
  [PlannerActivityStatus.Rejected]: 'Rejected',
  [PlannerActivityStatus.Cancelled]: 'Cancelled',
};

/**
 * Workflow-ordered status options for select dropdowns and toolbar filters.
 */
export const plannerActivityStatusOptions: PlannerActivityStatusValue[] = [
  PlannerActivityStatus.Scheduled,
  PlannerActivityStatus.InProgress,
  PlannerActivityStatus.CompletedPendingReview,
  PlannerActivityStatus.Approved,
  PlannerActivityStatus.Rejected,
  PlannerActivityStatus.Cancelled,
];

/**
 * Map backend status values to shared/ui Badge variants per the Wave 8 brief.
 *  - Scheduled              → secondary (waiting to start)
 *  - InProgress             → default (primary — work is live)
 *  - CompletedPendingReview → warning (awaiting supervisor approval)
 *  - Approved               → success (terminal good state)
 *  - Rejected               → destructive
 *  - Cancelled              → muted (terminal neutral)
 */
export const plannerActivityStatusBadgeVariant: Record<
  number,
  NonNullable<BadgeProps['variant']>
> = {
  [PlannerActivityStatus.Scheduled]: 'secondary',
  [PlannerActivityStatus.InProgress]: 'default',
  [PlannerActivityStatus.CompletedPendingReview]: 'warning',
  [PlannerActivityStatus.Approved]: 'success',
  [PlannerActivityStatus.Rejected]: 'destructive',
  [PlannerActivityStatus.Cancelled]: 'muted',
};

/**
 * Accent colour per status — used for the left-border accent on activity cards
 * and the row stripe in the execution review table.
 */
export const plannerActivityStatusAccent: Record<number, string> = {
  [PlannerActivityStatus.Scheduled]: '#5B4DFF',
  [PlannerActivityStatus.InProgress]: '#3F51B5',
  [PlannerActivityStatus.CompletedPendingReview]: '#B26A00',
  [PlannerActivityStatus.Approved]: '#1F9254',
  [PlannerActivityStatus.Rejected]: '#D32F2F',
  [PlannerActivityStatus.Cancelled]: '#6B7280',
};

/**
 * Zod literal-union for the PlannerActivityStatus integer values.
 *
 * Using `z.union([z.literal(1), ...])` (rather than `z.number().refine(...)`)
 * keeps `z.input` and `z.output` identical — avoids the well-known
 * react-hook-form Resolver mismatch (see MIGRATION_PLAYBOOK §3.5).
 */
export const plannerActivityStatusSchema = z.union([
  z.literal(PlannerActivityStatus.Scheduled),
  z.literal(PlannerActivityStatus.InProgress),
  z.literal(PlannerActivityStatus.CompletedPendingReview),
  z.literal(PlannerActivityStatus.Approved),
  z.literal(PlannerActivityStatus.Rejected),
  z.literal(PlannerActivityStatus.Cancelled),
]);

/**
 * Status changes that require the user to enter a comment / reason.
 *
 * The execution-review flow forces a reason on Reject so the supervisor's
 * rationale is captured.
 */
export function statusChangeRequiresComment(
  status: PlannerActivityStatusValue | number,
): boolean {
  return status === PlannerActivityStatus.Rejected;
}

/**
 * Is the activity in a terminal state (no further status transitions)?
 */
export function isTerminalStatus(
  status: PlannerActivityStatusValue | number | undefined,
): boolean {
  return (
    status === PlannerActivityStatus.Approved ||
    status === PlannerActivityStatus.Rejected ||
    status === PlannerActivityStatus.Cancelled
  );
}

/**
 * Is the activity awaiting supervisor review on the Execution page?
 */
export function isPendingReview(
  status: PlannerActivityStatusValue | number | undefined,
): boolean {
  return status === PlannerActivityStatus.CompletedPendingReview;
}
